/**
 * 서비스 관리 — 내장 서비스 토글(카드 + 스위치 + 세부설정) + 외부 서비스 연결(추가/삭제/토큰/테스트).
 * 사용자 친화 카드 UI. 내장 토글은 chat-server(SSOT) 위임, 외부 연결은 admin BFF 자체 저장소.
 */
import { useCallback, useEffect, useState } from 'react';
import './ServiceModules.css';
import {
  Mail, Calendar, CheckSquare, LayoutGrid, MessageCircle,
  Settings, Globe, Plus, Trash2, X,
} from '../../../icons';
import {
  listServiceModules, toggleServiceModule,
  type ServiceModuleItem, type ModuleKey,
} from '../../../api/adminConsole';
import {
  listExternalServices, createExternalService, updateExternalService,
  deleteExternalService, testExternalService, testExternalServiceDraft,
  listCustomServices, createCustomService, updateCustomService, deleteCustomService,
  type ExternalServiceView, type ServiceInput, type TokenScheme, type ServiceTestResult,
  type CustomService,
} from '../../../api/externalServices';
import { StyledSelect } from './MenuControls';
import { tAdmin, subscribeAdminLang, getAdminLang } from '../../../lib/admin-i18n';
import { getDateLocale } from '../../../lib/base-i18n';

const MODULE_LABEL_KEYS: Record<ModuleKey, string> = {
  mail: 'admin.module.label.mail',
  calendar: 'admin.module.label.calendar',
  task: 'admin.module.label.task',
  workspace: 'admin.module.label.workspace',
  workchat: 'admin.module.label.workchat',
};
const MODULE_ICONS: Record<ModuleKey, () => JSX.Element> = {
  mail: () => <Mail size={20} />,
  calendar: () => <Calendar size={20} />,
  task: () => <CheckSquare size={20} />,
  workspace: () => <LayoutGrid size={20} />,
  workchat: () => <MessageCircle size={20} />,
};

// 토글 스위치(접근성 input + 트랙/썸).
function Switch({ checked, disabled, onChange, label }: {
  checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string;
}) {
  return (
    <label className="svc-switch" title={label}>
      <input type="checkbox" checked={checked} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.checked)} />
      <span className="svc-switch__track" />
      <span className="svc-switch__thumb" />
    </label>
  );
}

export function ServiceModuleSection() {
  const [, setLangBump] = useState(getAdminLang());
  useEffect(() => subscribeAdminLang(() => setLangBump(getAdminLang())), []);
  return (
    <div className="svc">
      <div className="svc-head">
        <h2>{tAdmin('admin.module.heading')}</h2>
        <p>{tAdmin('admin.module.subheading')}</p>
      </div>
      <InternalModules />
      <CustomServices />
      <ExternalServices />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  내장 서비스 토글 (mail/calendar/task/workspace/workchat)
// ════════════════════════════════════════════════════════════════
function InternalModules() {
  const [items, setItems] = useState<ServiceModuleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<ModuleKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editKey, setEditKey] = useState<ModuleKey | null>(null);
  const [configDraft, setConfigDraft] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems(await listServiceModules()); }
    catch (e: any) { setError(e?.response?.data?.message ?? '서비스 목록 불러오기 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  function mapErr(e: any, fallback: string): string {
    const data = e?.response?.data;
    if (data?.code === 'GRACE_ACTIVE') return '회사 가입 후 24시간 동안은 변경이 제한됩니다.';
    if (data?.code === 'INSTITUTION_FROZEN') return '회사가 freeze 상태입니다.';
    return data?.message ?? fallback;
  }

  async function handleToggle(moduleKey: ModuleKey, enabled: boolean) {
    setBusyKey(moduleKey); setError(null);
    try {
      const updated = await toggleServiceModule(moduleKey, { enabled });
      setItems((arr) => arr.map((i) => (i.moduleKey === moduleKey ? { ...i, enabled: updated.enabled, updatedAt: updated.updatedAt } : i)));
    } catch (e: any) { setError(mapErr(e, '저장 실패')); }
    finally { setBusyKey(null); }
  }

  function openConfig(it: ServiceModuleItem) {
    setEditKey(it.moduleKey);
    setConfigDraft(JSON.stringify(it.configuration ?? {}, null, 2));
    setConfigError(null);
  }
  async function saveConfig(moduleKey: ModuleKey) {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(configDraft || '{}');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('object 형식이어야 합니다');
    } catch (e: any) { setConfigError(`JSON 파싱 오류: ${e?.message ?? '형식을 확인하세요'}`); return; }
    setBusyKey(moduleKey); setConfigError(null);
    try {
      const updated = await toggleServiceModule(moduleKey, { configuration: parsed });
      setItems((arr) => arr.map((i) => (i.moduleKey === moduleKey ? { ...i, configuration: updated.configuration, updatedAt: updated.updatedAt } : i)));
      setEditKey(null);
    } catch (e: any) { setConfigError(mapErr(e, '설정 저장 실패')); }
    finally { setBusyKey(null); }
  }

  return (
    <section className="svc-cat svc-cat--internal">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Settings size={17} /></span>
        <span className="svc-cat__title">내장 서비스</span>
        <span className="svc-cat__count">{items.length}</span>
        <span className="svc-cat__desc">메일·캘린더·할일·워크스페이스·채팅 모듈 on/off</span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {loading ? (
        <p className="svc-empty">{tAdmin('admin.llm.loading')}</p>
      ) : (
        <div className="svc-grid">
          {items.map((it) => {
            const Icon = MODULE_ICONS[it.moduleKey] ?? (() => <Settings size={20} />);
            const busy = busyKey === it.moduleKey;
            return (
              <div key={it.moduleKey} className={`svc-card${it.enabled ? '' : ' is-off'}`}>
                <div className="svc-card__top">
                  <span className="svc-card__icon"><Icon /></span>
                  <span className="svc-card__name">{tAdmin((MODULE_LABEL_KEYS[it.moduleKey] ?? it.moduleKey) as any)}</span>
                  <Switch
                    checked={it.enabled}
                    disabled={busy}
                    onChange={(v) => void handleToggle(it.moduleKey, v)}
                    label={it.enabled ? tAdmin('admin.module.action.disable') : tAdmin('admin.module.action.enable')}
                  />
                </div>
                <div className="svc-card__chips">
                  <span className="svc-card__status">
                    <span className={`svc-dot ${it.enabled ? 'svc-dot--on' : 'svc-dot--off'}`} />
                    {it.enabled ? tAdmin('admin.module.status.enabled') : tAdmin('admin.module.status.disabled')}
                  </span>
                </div>
                <div className="svc-card__foot">
                  <span className="svc-card__meta">
                    {it.updatedAt ? new Date(it.updatedAt).toLocaleString(getDateLocale()) : '—'}
                  </span>
                  <button type="button" className="svc-btn" style={{ marginLeft: 'auto' }} disabled={busy}
                    onClick={() => (editKey === it.moduleKey ? setEditKey(null) : openConfig(it))} aria-expanded={editKey === it.moduleKey}>
                    세부설정
                  </button>
                </div>
                {editKey === it.moduleKey && (
                  <div className="svc-config" data-testid={`config-editor-${it.moduleKey}`}>
                    <span className="svc-field__hint">
                      세부설정(JSON)은 이 모듈의 회사별 옵션을 담는 자유형 설정입니다. 키 형식 제한이 없으며,
                      모듈 on/off 는 위 스위치가 담당합니다. (예: {'{ "retentionDays": 90 }'})
                    </span>
                    <textarea rows={6} value={configDraft} spellCheck={false} aria-label={`${it.moduleKey} configuration JSON`}
                      onChange={(e) => setConfigDraft(e.target.value)} />
                    {configError && <div className="admin-console__alert admin-console__alert--warn" role="alert">{configError}</div>}
                    <button type="button" className="svc-mbtn svc-mbtn--primary" disabled={busy} onClick={() => void saveConfig(it.moduleKey)}>
                      설정 저장
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
//  사용자 정의 서비스 (추가/삭제/토글/설정) — admin BFF 자체 레지스트리
//  ※ 플랫폼 내장 모듈(고정 enum)은 chat-server SSOT 라 신규 추가 불가 → 관리용 항목으로 제공.
// ════════════════════════════════════════════════════════════════
function CustomServices() {
  const [list, setList] = useState<CustomService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<CustomService | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setList(await listCustomServices()); }
    catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? '목록 불러오기 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function toggle(svc: CustomService) {
    setBusyId(svc.id);
    try {
      const u = await updateCustomService(svc.id, { enabled: !svc.enabled });
      setList((arr) => arr.map((s) => (s.id === svc.id ? u : s)));
    } catch (e: any) { setError(e?.response?.data?.error ?? '변경 실패'); }
    finally { setBusyId(null); }
  }
  async function remove(svc: CustomService) {
    if (!window.confirm(`"${svc.name}" 서비스를 삭제할까요?`)) return;
    setBusyId(svc.id);
    try { await deleteCustomService(svc.id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  return (
    <section className="svc-cat svc-cat--custom">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Plus size={17} /></span>
        <span className="svc-cat__title">사용자 정의 서비스</span>
        <span className="svc-cat__count">{list.length}</span>
        <span className="svc-cat__desc">관리자가 추가하는 서비스 항목 (이름·설명·설정 JSON·on/off)</span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <button type="button" className="svc-add" onClick={() => { setEditing(null); setShowAdd(true); }}>
        <span className="svc-add__icon"><Plus size={20} /></span>
        <span>
          <span className="svc-add__title">서비스 추가</span>
          <span className="svc-add__desc">새 서비스 항목을 등록합니다. 이름·설명·세부설정(JSON)을 지정할 수 있습니다.</span>
        </span>
      </button>

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : list.length === 0 ? (
        <p className="svc-empty">추가된 사용자 정의 서비스가 없습니다.</p>
      ) : (
        <div className="svc-grid">
          {list.map((s) => {
            const busy = busyId === s.id;
            return (
              <div key={s.id} className={`svc-card${s.enabled ? '' : ' is-off'}`}>
                <div className="svc-card__top">
                  <span className="svc-card__icon"><Settings size={20} /></span>
                  <span className="svc-card__name" title={s.name}>{s.name}</span>
                  <Switch checked={s.enabled} disabled={busy} onChange={() => void toggle(s)} label={s.enabled ? '비활성화' : '활성화'} />
                </div>
                {s.description && <div className="svc-card__detail" style={{ fontFamily: 'inherit' }}>{s.description}</div>}
                <div className="svc-card__chips">
                  <span className="svc-card__status">
                    <span className={`svc-dot ${s.enabled ? 'svc-dot--on' : 'svc-dot--off'}`} />
                    {s.enabled ? '활성' : '비활성'}
                  </span>
                  <span className="svc-card__chip">설정 {Object.keys(s.configuration ?? {}).length}개 키</span>
                </div>
                <div className="svc-card__foot">
                  <button type="button" className="svc-btn" onClick={() => { setEditing(s); setShowAdd(true); }}>편집</button>
                  <button type="button" className="svc-btn svc-btn--danger" disabled={busy} onClick={() => void remove(s)} aria-label="삭제">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <CustomServiceModal
          existing={editing}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); void reload(); }}
        />
      )}
    </section>
  );
}

/** ⚠ 2026-09-09 — 새 목록 규격 화면(ServiceListSection)이 그대로 연다. 폼을 두 벌로 만들지 않는다. */
export function CustomServiceModal({ existing, onClose, onSaved }: {
  existing: CustomService | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [configDraft, setConfigDraft] = useState(JSON.stringify(existing?.configuration ?? {}, null, 2));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  async function save() {
    let cfg: Record<string, unknown>;
    try {
      cfg = JSON.parse(configDraft || '{}');
      if (typeof cfg !== 'object' || cfg === null || Array.isArray(cfg)) throw new Error('object 형식이어야 합니다');
    } catch (e: any) { setErr(`설정 JSON 오류: ${e?.message ?? '형식 확인'}`); return; }
    setSaving(true); setErr(null);
    try {
      const payload = { name: name.trim(), description: description.trim(), enabled, configuration: cfg };
      if (existing) await updateCustomService(existing.id, payload);
      else await createCustomService(payload);
      onSaved();
    } catch (e: any) { setErr(e?.response?.data?.error ?? e?.message ?? '저장 실패'); }
    finally { setSaving(false); }
  }

  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="svc-modal" role="dialog" aria-modal="true" aria-label="사용자 정의 서비스" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3>{existing ? '서비스 편집' : '서비스 추가'}</h3>
          <button type="button" className="svc-modal__close" onClick={onClose} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          <div className="svc-field">
            <label>서비스 이름 *</label>
            <input value={name} placeholder="예: 사내 공지 봇" onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="svc-field">
            <label>설명 (선택)</label>
            <input value={description} placeholder="서비스 용도 한 줄 설명" onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="svc-field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              사용 여부
              <Switch checked={enabled} onChange={setEnabled} label={enabled ? '비활성화' : '활성화'} />
            </label>
          </div>
          <div className="svc-field">
            <label>세부설정 (JSON)</label>
            <textarea rows={6} value={configDraft} spellCheck={false} aria-label="configuration JSON"
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.76rem', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', resize: 'vertical' }}
              onChange={(e) => setConfigDraft(e.target.value)} />
            <span className="svc-field__hint">서비스 옵션을 담는 자유형 JSON 객체입니다. 키 제한 없음. (예: {'{ "webhookUrl": "https://..." }'})</span>
          </div>
          {err && <div className="svc-test svc-test--bad" role="alert"><span className="svc-dot svc-dot--bad" />{err}</div>}
        </div>
        <div className="svc-modal__foot">
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose}>취소</button>
          <button type="button" className="svc-mbtn svc-mbtn--primary" onClick={() => void save()} disabled={!name.trim() || saving}>
            {saving ? '저장 중…' : existing ? '저장' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  외부 서비스 연결 (추가/삭제/토큰/테스트) — admin BFF 자체 저장소
// ════════════════════════════════════════════════════════════════
function ExternalServices() {
  const [list, setList] = useState<ExternalServiceView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, ServiceTestResult | 'loading'>>({});
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<ExternalServiceView | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setList(await listExternalServices()); }
    catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? '목록 불러오기 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function toggle(svc: ExternalServiceView) {
    setBusyId(svc.id);
    try {
      const u = await updateExternalService(svc.id, { enabled: !svc.enabled });
      setList((arr) => arr.map((s) => (s.id === svc.id ? u : s)));
    } catch (e: any) { setError(e?.response?.data?.error ?? '변경 실패'); }
    finally { setBusyId(null); }
  }
  async function runTest(id: string) {
    setTests((t) => ({ ...t, [id]: 'loading' }));
    try {
      const r = await testExternalService(id);
      setTests((t) => ({ ...t, [id]: r }));
    } catch (e: any) {
      setTests((t) => ({ ...t, [id]: { ok: false, latency_ms: 0, detail: e?.message ?? '테스트 실패' } }));
    }
  }
  async function remove(svc: ExternalServiceView) {
    if (!window.confirm(`"${svc.name}" 연결을 삭제할까요?`)) return;
    setBusyId(svc.id);
    try { await deleteExternalService(svc.id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  return (
    <section className="svc-cat svc-cat--external">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><Globe size={17} /></span>
        <span className="svc-cat__title">외부 서비스 연결</span>
        <span className="svc-cat__count">{list.length}</span>
        <span className="svc-cat__desc">외부 업무 서버 + 보안 토큰 등록·연결 테스트</span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <button type="button" className="svc-add" onClick={() => { setEditing(null); setShowAdd(true); }}>
        <span className="svc-add__icon"><Plus size={20} /></span>
        <span>
          <span className="svc-add__title">서비스 추가</span>
          <span className="svc-add__desc">외부 서버 URL과 보안 토큰을 입력해 새 연결을 등록합니다.</span>
        </span>
      </button>

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : list.length === 0 ? (
        <p className="svc-empty">등록된 외부 서비스가 없습니다.</p>
      ) : (
        <div className="svc-grid">
          {list.map((s) => {
            const busy = busyId === s.id;
            const t = tests[s.id];
            const dot = t === 'loading' ? 'svc-dot--loading'
              : t == null ? (s.enabled ? 'svc-dot--on' : 'svc-dot--off')
              : t.ok ? 'svc-dot--on' : 'svc-dot--bad';
            const statusText = t === 'loading' ? '테스트 중…'
              : t == null ? (s.enabled ? '활성' : '비활성')
              : t.ok ? `정상 ${t.latency_ms}ms` : '연결 실패';
            return (
              <div key={s.id} className={`svc-card${s.enabled ? '' : ' is-off'}`}>
                <div className="svc-card__top">
                  <span className="svc-card__icon"><Globe size={20} /></span>
                  <span className="svc-card__name" title={s.name}>{s.name}</span>
                  <Switch checked={s.enabled} disabled={busy} onChange={() => void toggle(s)} label={s.enabled ? '비활성화' : '활성화'} />
                </div>
                <div className="svc-card__detail">{s.base_url}{s.health_path}</div>
                <div className="svc-card__chips">
                  <span className="svc-card__status"><span className={`svc-dot ${dot}`} />{statusText}</span>
                  {s.has_token
                    ? <span className="svc-card__chip">토큰 {s.token_preview} · {s.token_scheme}</span>
                    : <span className="svc-card__chip">토큰 없음</span>}
                  {t && t !== 'loading' && !t.ok && <span className="svc-card__chip" title={t.detail}>{t.detail.slice(0, 40)}</span>}
                </div>
                <div className="svc-card__foot">
                  <button type="button" className="svc-btn" disabled={t === 'loading'} onClick={() => void runTest(s.id)}>연결 테스트</button>
                  <button type="button" className="svc-btn" onClick={() => { setEditing(s); setShowAdd(true); }}>편집</button>
                  <button type="button" className="svc-btn svc-btn--danger" disabled={busy} onClick={() => void remove(s)} aria-label="삭제">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <ExternalServiceModal
          existing={editing}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); void reload(); }}
        />
      )}
    </section>
  );
}

// ─── 추가/편집 모달 ──────────────────────────────────────────────
/** ⚠ 2026-09-09 — 새 목록 규격 화면(ServiceListSection)이 그대로 연다. */
export function ExternalServiceModal({ existing, onClose, onSaved }: {
  existing: ExternalServiceView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(existing?.base_url ?? '');
  const [healthPath, setHealthPath] = useState(existing?.health_path ?? '');
  const [scheme, setScheme] = useState<TokenScheme>(existing?.token_scheme ?? 'bearer');
  const [header, setHeader] = useState(existing?.token_header ?? 'Authorization');
  const [token, setToken] = useState('');
  const [test, setTest] = useState<ServiceTestResult | 'loading' | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const canSubmit = name.trim().length > 0 && /^https?:\/\//i.test(baseUrl.trim());

  function payload(): ServiceInput {
    return {
      name: name.trim(), base_url: baseUrl.trim(),
      token_header: header.trim() || 'Authorization',
      token_scheme: scheme,
      ...(token.trim() ? { token: token.trim() } : {}),
      health_path: healthPath.trim(),
    };
  }

  async function runTest() {
    setTest('loading'); setErr(null);
    try { setTest(await testExternalServiceDraft(payload())); }
    catch (e: any) { setTest({ ok: false, latency_ms: 0, detail: e?.message ?? '테스트 실패' }); }
  }
  async function save() {
    setSaving(true); setErr(null);
    try {
      if (existing) await updateExternalService(existing.id, payload());
      else await createExternalService(payload());
      onSaved();
    } catch (e: any) { setErr(e?.response?.data?.error ?? e?.message ?? '저장 실패'); }
    finally { setSaving(false); }
  }

  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="svc-modal" role="dialog" aria-modal="true" aria-label="외부 서비스 연결" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3>{existing ? '서비스 편집' : '외부 서비스 추가'}</h3>
          <button type="button" className="svc-modal__close" onClick={onClose} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          <div className="svc-field">
            <label>서비스 이름 *</label>
            <input value={name} placeholder="예: 사내 ERP" onChange={(e) => { setName(e.target.value); setTest(null); }} />
          </div>
          <div className="svc-field">
            <label>서버 URL (Base URL) *</label>
            <input value={baseUrl} placeholder="https://erp.company.com/api" onChange={(e) => { setBaseUrl(e.target.value); setTest(null); }} />
          </div>
          <div className="svc-field">
            <label>헬스 경로 (선택)</label>
            <input value={healthPath} placeholder="/health (연결 테스트 시 붙일 경로)" onChange={(e) => setHealthPath(e.target.value)} />
          </div>
          <div className="svc-row2">
            <div className="svc-field">
              <label>토큰 방식</label>
              <StyledSelect value={scheme} onChange={(v) => setScheme(v as TokenScheme)} options={[
                { value: 'bearer', label: 'Bearer (Authorization)' },
                { value: 'raw', label: '헤더 원문' },
                { value: 'none', label: '토큰 없음' },
              ]} />
            </div>
            <div className="svc-field">
              <label>헤더 이름</label>
              <input value={header} disabled={scheme === 'none'} placeholder="Authorization / X-API-Key" onChange={(e) => setHeader(e.target.value)} />
            </div>
          </div>
          {scheme !== 'none' && (
            <div className="svc-field">
              <label>보안 토큰{existing?.has_token ? ' (비워두면 기존 유지)' : ''}</label>
              <input type="password" value={token} autoComplete="off"
                placeholder={existing?.has_token ? `기존: ${existing.token_preview}` : 'API 토큰 / 키'}
                onChange={(e) => { setToken(e.target.value); setTest(null); }} />
              <span className="svc-field__hint">토큰은 서버에 mode 600 파일로 저장되고 조회 시 마스킹됩니다.</span>
            </div>
          )}

          {test && test !== 'loading' && (
            <div className={`svc-test ${test.ok ? 'svc-test--ok' : 'svc-test--bad'}`} role="status">
              <span className={`svc-dot ${test.ok ? 'svc-dot--on' : 'svc-dot--bad'}`} />
              {test.ok ? `연결 정상 · ${test.detail} · ${test.latency_ms}ms` : `연결 실패: ${test.detail}`}
            </div>
          )}
          {test === 'loading' && <div className="svc-test svc-test--ok"><span className="svc-dot svc-dot--loading" /> 테스트 중…</div>}
          {err && <div className="svc-test svc-test--bad" role="alert"><span className="svc-dot svc-dot--bad" />{err}</div>}
        </div>
        <div className="svc-modal__foot">
          <button type="button" className="svc-mbtn" onClick={() => void runTest()} disabled={!canSubmit || test === 'loading'}>
            {test === 'loading' ? '테스트 중…' : '연결 테스트'}
          </button>
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose}>취소</button>
          <button type="button" className="svc-mbtn svc-mbtn--primary" onClick={() => void save()} disabled={!canSubmit || saving}>
            {saving ? '저장 중…' : existing ? '저장' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
}
